import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleCheckIcon, LoaderIcon, LockIcon } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { ui } from "../data/ui";
import { useFormValidation } from "../hooks/useFormValidation";
import { SERVICE_REQUEST_EVENT, type ServiceRequestDetail } from "../utils/serviceRequest";
import { email, phone, required } from "../utils/validation";
import { FormField } from "./FormField";
type RequiredKey = "businessName" | "contactName" | "phone" | "email";
const rules = {
  businessName: required,
  contactName: required,
  phone,
  email
};
const copy = {
  businessName: {
    en: "Business name",
    es: "Nombre del negocio"
  },
  contactName: {
    en: "Contact name",
    es: "Nombre de contacto"
  },
  phone: {
    en: "Phone number",
    es: "Teléfono"
  },
  email: {
    en: "Email",
    es: "Correo electrónico"
  },
  service: {
    en: "Service needed",
    es: "Servicio requerido"
  },
  needs: {
    en: "Describe your needs",
    es: "Describa sus necesidades"
  },
  needsPlaceholder: {
    en: "Location, size of space, frequency, start date…",
    es: "Ubicación, tamaño del espacio, frecuencia, fecha de inicio…"
  },
  language: {
    en: "Preferred language",
    es: "Idioma preferido"
  },
  serviceOptions: [{
    value: "cleaning",
    label: {
      en: "Cleaning",
      es: "Limpieza"
    }
  }, {
    value: "staffing",
    label: {
      en: "Staffing",
      es: "Personal"
    }
  }, {
    value: "hospitality",
    label: {
      en: "Hospitality",
      es: "Hospitalidad"
    }
  }, {
    value: "custom",
    label: {
      en: "Custom solution",
      es: "Solución a medida"
    }
  }]
};
export function ContactForm() {
  const {
    t,
    lang
  } = useLanguage();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const {
    values,
    handleChange,
    handleBlur,
    setValue,
    visibleError,
    validateAll,
    reset
  } = useFormValidation({
    businessName: "",
    contactName: "",
    phone: "",
    email: "",
    serviceType: "cleaning",
    needs: "",
    language: lang
  }, rules);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  useEffect(() => {
    const onRequest = (e: Event) => {
      const {
        type,
        note
      } = (e as CustomEvent<ServiceRequestDetail>).detail;
      setStatus(s => s === "success" ? "idle" : s);
      setValue("serviceType", type);
      if (note && !valuesRef.current.needs.trim()) setValue("needs", `${note}: `);
    };
    window.addEventListener(SERVICE_REQUEST_EVENT, onRequest);
    return () => window.removeEventListener(SERVICE_REQUEST_EVENT, onRequest);
  }, [setValue]);
  const err = (k: RequiredKey) => {
    const e = visibleError(k);
    return e ? t(e) : null;
  };
  const inputProps = (k: RequiredKey) => ({
    id: `contact-${k}`,
    name: k,
    value: values[k],
    onChange: handleChange,
    onBlur: handleBlur,
    "aria-invalid": Boolean(err(k)),
    "aria-describedby": err(k) ? `contact-${k}-error` : undefined,
    className: `field ${err(k) ? "field-error" : ""}`
  });
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Obtén los valores del formulario
    const businessName = (document.querySelector('input[name="businessName"]') as HTMLInputElement)?.value || '';
    const contactName = (document.querySelector('input[name="contactName"]') as HTMLInputElement)?.value || '';
    const phone = (document.querySelector('input[name="phone"]') as HTMLInputElement)?.value || '';
    const email = (document.querySelector('input[name="email"]') as HTMLInputElement)?.value || '';
    const service = (document.querySelector('select[name="service"]') as HTMLSelectElement)?.value || '';
    const needs = (document.querySelector('textarea[name="needs"]') as HTMLTextAreaElement)?.value || '';

    // Crea el mensaje para WhatsApp
    const message = `Hola, soy ${contactName}%0A%0ANegocio: ${businessName}%0ATeléfono: ${phone}%0AEmail: ${email}%0AServicio: ${service}%0A%0aMensaje:%0A${needs}`;

    // Redirige a WhatsApp
    const whatsappNumber = "+19259173657";
    window.location.href = `https://wa.me/${whatsappNumber}?text=${message}`;

    // Muestra mensaje de éxito
    setStatus("success");
    reset();

    // Resetea después de 5 segundos
    setTimeout(() => setStatus("submitting"), 5000);
  };
  return <div className="border border-gold/40 bg-charcoal p-6 sm:p-8 lg:p-10" data-id="element-76" data-mp="35y8kt677yqb5">
      <h3 className="font-display text-2xl font-bold uppercase text-white" data-id="element-77" data-mp="35w5jhq0m9ar4">{t(ui.contact.formTitle)}</h3>

      <AnimatePresence mode="wait" initial={false} data-id="element-78" data-mp="6h0r4ype7g0o">
        {status === "success" ? <motion.div key="success" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} role="status" className="mt-8 flex flex-col items-start gap-4 border-l-2 border-gold bg-ink p-6" data-id="element-79" data-mp="2ib4sgmdmo4w">
            <CircleCheckIcon className="h-9 w-9 text-gold" aria-hidden="true" data-id="element-80" data-mp="2d91ngaji46rh" />
            <p className="font-display text-2xl font-semibold text-white" data-id="element-81" data-mp="235nyvq79au44">{t(ui.contact.success)}</p>
            <button type="button" onClick={() => setStatus("idle")} className="link-slide text-sm font-semibold text-gold" data-id="element-82" data-mp="284qtshtivjyd">
              {t(ui.contact.another)}
            </button>
          </motion.div> : <motion.form key="form" ref={formRef} noValidate onSubmit={onSubmit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-83" data-mp="2hv2a73ue7lm5">
            <FormField id="contact-businessName" label={t(copy.businessName)} required error={err("businessName")} data-id="element-84" data-mp="2vv36s73mk6vt">
              <input type="text" autoComplete="organization" {...inputProps("businessName")} data-id="element-85" data-mp="30r23qj30qx7u" />
            </FormField>
            <FormField id="contact-contactName" label={t(copy.contactName)} required error={err("contactName")} data-id="element-86" data-mp="16f3tcdsz3vad">
              <input type="text" autoComplete="name" {...inputProps("contactName")} data-id="element-87" data-mp="ye5xgfo9q44v" />
            </FormField>
            <FormField id="contact-phone" label={t(copy.phone)} required error={err("phone")} data-id="element-88" data-mp="tge89tf0cw4d">
              <input type="tel" autoComplete="tel" {...inputProps("phone")} data-id="element-89" data-mp="18d57o2oom7tz" />
            </FormField>
            <FormField id="contact-email" label={t(copy.email)} required error={err("email")} data-id="element-90" data-mp="13disvot7gjj2">
              <input type="email" autoComplete="email" {...inputProps("email")} data-id="element-91" data-mp="1hcrzbwrlzz9u" />
            </FormField>
            <FormField id="contact-serviceType" label={t(copy.service)} className="sm:col-span-2" data-id="element-92" data-mp="1cf8icp3j18e7">
              <select id="contact-serviceType" name="serviceType" value={values.serviceType} onChange={handleChange} className="field" data-id="element-93" data-mp="1r3mp3b42dh01">
                {copy.serviceOptions.map(o => <option key={o.value} value={o.value} data-id="element-94" data-mp="203jcl9epw9ti">{t(o.label)}</option>)}
              </select>
            </FormField>
            <FormField id="contact-needs" label={t(copy.needs)} className="sm:col-span-2" data-id="element-95" data-mp="1w4an4gqpmy3r">
              <textarea id="contact-needs" name="needs" rows={4} value={values.needs} onChange={handleChange} placeholder={t(copy.needsPlaceholder)} className="field resize-y" data-id="element-96" data-mp="2er9qnpp71rmd" />
            </FormField>
            <fieldset className="sm:col-span-2" data-id="element-97" data-mp="2aqzl2e5bd381">
              <legend className="mb-1.5 text-sm font-medium text-white/90" data-id="element-98" data-mp="1vz7g8w35b7kk">{t(copy.language)}</legend>
              <div className="flex gap-3" data-id="element-99" data-mp="20s0jtgmvo9v2">
                {[{
              v: "en",
              l: "English"
            }, {
              v: "es",
              l: "Español"
            }].map(o => <label key={o.v} className={`flex min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded-sm border text-sm font-medium transition-colors duration-150 ease-out focus-within:ring-2 focus-within:ring-gold ${values.language === o.v ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-white/80 hover:border-white/40"}`} data-id="element-100" data-mp="1n1ezdnhrzrv2">
                    <input type="radio" name="language" value={o.v} checked={values.language === o.v} onChange={handleChange} className="sr-only" data-id="element-101" data-mp="1xiqu62xf0gme" />
                    {o.l}
                  </label>)}
              </div>
            </fieldset>
            {status === "error" && <p role="alert" className="text-sm text-red-300 sm:col-span-2" data-id="element-102" data-mp="1jjiu77tfh4h5">{t(ui.common.submitError)}</p>}
            <button type="submit" disabled={status === "submitting"} className="btn-gold h-14 text-base sm:col-span-2 disabled:opacity-70" data-id="element-103" data-mp="1ekemklm5jesx">
              {status === "submitting" ? <>
                  <LoaderIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-104" data-mp="14ucw5n6btkau" />
                  {t(ui.common.sending)}
                </> : t(ui.common.getEstimate)}
            </button>
            <p className="flex items-start gap-2 text-xs text-smoke sm:col-span-2" data-id="element-105" data-mp="qu24wj13u3p5">
              <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden="true" data-id="element-106" data-mp="qwekwqnay3zk" />
              {t(ui.contact.privacy)}
            </p>
          </motion.form>}
      </AnimatePresence>
    </div>;
}